export type OrderShippingAddress = {
    name: string | null;
    line1: string | null;
    line2: string | null;
    city: string | null;
    state: string | null;
    postalCode: string | null;
    country: string | null;
};

/** Formats an amount in minor units, e.g. 6313 usd → "$63.13". */
export function formatOrderMoney(cents: number, currency: string, locale = 'en-US'): string {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
}

/** Address lines in mailing-label order, skipping empty parts. */
export function formatAddressLines(address: OrderShippingAddress): string[] {
    const lines: string[] = [];
    for (const value of [address.name, address.line1, address.line2]) {
        if (value != null && value.trim().length > 0) lines.push(value.trim());
    }
    const city = address.city?.trim() ?? '';
    const region = [address.state?.trim() ?? '', address.postalCode?.trim() ?? ''].filter(part => part.length > 0).join(' ');
    const cityLine = city.length > 0 && region.length > 0 ? `${city}, ${region}` : city || region;
    if (cityLine.length > 0) lines.push(cityLine);
    if (address.country != null && address.country.trim().length > 0) lines.push(address.country.trim());
    return lines;
}

/**
 * Formats a calendar date sent as yyyy-MM-dd without shifting it through the browser's time zone,
 * e.g. "2026-10-15" → "Thu, Oct 15, 2026".
 */
export function formatDateOnly(value: string, locale = 'en-US'): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (match == null) return value;
    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
    return date.toLocaleDateString(locale, { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

const channelNames: Record<string, string> = {
    google: 'Google',
    bing: 'Bing',
    pinterest: 'Pinterest',
    instagram: 'Instagram',
    facebook: 'Facebook',
    other: 'Another website',
    direct: 'Direct (no referring site)',
    unknown: 'Unknown'
};

/**
 * Where an order's buyer came from, e.g. "pinterest" → "Pinterest", or "qr:k3x9p2" with its link's label →
 * "QR: Bristol café". For "other", the referring site's host is added when the order recorded one: "Another website
 * (example-blog.com)", shown as text only. Null when the order has no channel (orders from before channels were
 * recorded, and reprints).
 */
export function formatAcquisitionChannel(
    channel: string | null,
    campaignLabel: string | null,
    referrerHost: string | null = null
): string | null {
    if (channel == null || channel.length === 0) return null;
    if (channel.startsWith('qr:')) return `QR: ${campaignLabel ?? channel.slice(3)}`;
    const name = channelNames[channel] ?? channel;
    const host = referrerHost?.trim() ?? '';
    return channel === 'other' && host.length > 0 ? `${name} (${host})` : name;
}

/** Today's date as yyyy-MM-dd in the browser's local calendar, for a date input's default and maximum. */
export function getLocalDateInputValue(now = new Date()): string {
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
}
